/**
 * SearchFilter plugin - fulltext search filter for grant calls.
 *
 * URL param: search
 * API param: search (same as URL)
 * SavedView field: search
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useRef, useEffect, useCallback } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";

/**
 * Search filter React component.
 *
 * Uses an UNCONTROLLED input (defaultValue + ref) to avoid focus loss
 * and character garbling caused by React's controlled reconciliation
 * during parent re-renders (triggered by useSearchParams).
 *
 * Value management:
 * - DOM input manages its own value (no React `value` prop)
 * - Debounced onChange propagates typed value to parent after 400ms
 * - Parent value synced to DOM only when input is NOT focused
 */
const SearchFilterComponent = React.memo(function SearchFilterComponent(
  props: FilterComponentProps<string>
) {
  const { value, onChange } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync from parent only when input is not focused.
  // Cancel pending debounce to prevent race condition where old value
  // re-applies after parent clears filters.
  useEffect(() => {
    if (!isFocusedRef.current && inputRef.current) {
      clearTimeout(debounceTimerRef.current);
      inputRef.current.value = value;
    }
  }, [value]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onChangeRef.current(newValue);
      }, 400);
    },
    []
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    // Sync current DOM value to parent on blur, but only if it differs
    // from the parent value. Skipping redundant onChange prevents a race
    // condition when clicking "Wyczyść" (blur fires onChange with old value
    // right before clearAllFilters clears URL params).
    if (inputRef.current) {
      const currentValue = inputRef.current.value;
      clearTimeout(debounceTimerRef.current);
      if (currentValue !== valueRef.current) {
        onChangeRef.current(currentValue);
      }
    }
  }, []);

  return React.createElement(
    "div",
    { className: "relative flex-1 min-w-[200px] max-w-[300px]" },
    React.createElement("input", {
      ref: inputRef,
      type: "text",
      defaultValue: value,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      placeholder: "Szukaj...",
      title: "Wyszukuje po tytule, operatorze, projekcie, konkursie i programie",
      className:
        "w-full bg-white border border-neutral-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
      "aria-label": "Szukaj naborów",
    }),
    React.createElement(
      "div",
      {
        className:
          "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-neutral-400",
      },
      React.createElement(
        "svg",
        {
          className: "h-4 w-4",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24",
          "aria-hidden": "true",
        },
        React.createElement("path", {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: 2,
          d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
        })
      )
    )
  );
});

export const searchFilter: FilterPlugin<string> = {
  key: "search",
  label: "Szukaj",
  placement: "main",
  order: 7,
  defaultValue: "",
  Component: SearchFilterComponent,

  isActive(value: string): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string, params: URLSearchParams): void {
    params.set("search", value);
  },

  fromUrlParams(params: URLSearchParams): string {
    return params.get("search") || "";
  },

  toSavedView(value: string, filters: Partial<SavedViewFilters>): void {
    filters.search = value;
  },

  fromSavedView(filters: SavedViewFilters): string {
    return filters.search ?? "";
  },

  toApiParams(value: string, params: URLSearchParams): void {
    params.set("search", value);
  },
};
